import { useState } from 'react';
import {
  alpha,
  Box,
  ClickAwayListener,
  IconButton,
  ListItemButton,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useRecoilValue } from 'recoil';
import { watchlistsState } from 'states';
import { SGTooltip } from 'components/core';

interface CanvasGroupWatchlistButtonProps {
  /** Disabled when there's no target group. */
  disabled?: boolean;
  /** Symbols valid for a group (equities ∩ HIRO); others render disabled. */
  validSymbols: Set<string>;
  /** Apply the chosen symbol to the active group. */
  onPick: (sym: string) => void;
}

/**
 * Eye-icon dropdown that lists the user's watchlists with their symbols. Picking
 * a symbol applies it to the active group. Symbols outside the group-valid
 * universe are shown but disabled so the watchlist still reads as complete.
 */
export const CanvasGroupWatchlistButton = ({
  disabled = false,
  validSymbols,
  onPick,
}: CanvasGroupWatchlistButtonProps) => {
  const theme = useTheme();
  const watchlists = useRecoilValue(watchlistsState);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl != null;
  const close = () => setAnchorEl(null);

  return (
    <>
      <SGTooltip title="Apply a symbol from a watchlist" placement="top">
        <span>
          <IconButton
            data-ph-capture-attribute-element="canvas-group-watchlist"
            size="small"
            disabled={disabled}
            onClick={(e) => setAnchorEl(open ? null : e.currentTarget)}
            aria-label="Pick a symbol from a watchlist"
            sx={{
              p: 0.5,
              color: open
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            {open ? (
              <VisibilityIcon sx={{ fontSize: 18 }} />
            ) : (
              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </span>
      </SGTooltip>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <ClickAwayListener onClickAway={close}>
          <Box
            sx={{
              mt: 0.5,
              minWidth: 220,
              maxWidth: 280,
              maxHeight: 360,
              overflowY: 'auto',
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: theme.palette.shadows.paperBoxShadow,
            }}
          >
            {watchlists == null || watchlists.length === 0 ? (
              <Typography
                sx={{
                  px: 1.5,
                  py: 1,
                  fontSize: 12.5,
                  color: theme.palette.text.secondary,
                }}
              >
                No watchlists yet.
              </Typography>
            ) : (
              watchlists.map((watchlist, idx) => (
                <Box
                  key={watchlist.id ?? watchlist.name}
                  sx={{
                    pt: idx === 0 ? 0 : 1,
                    px: 3,
                    mt: idx === 0 ? 0 : 0.75,
                    borderTop:
                      idx === 0
                        ? 'none'
                        : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography
                    sx={{
                      px: 1.25,
                      pb: 0.5,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {watchlist.name}
                  </Typography>
                  {watchlist.symbols.length === 0 ? (
                    <Typography
                      sx={{
                        px: 1.75,
                        py: 0.5,
                        fontSize: 12,
                        fontStyle: 'italic',
                        color: theme.palette.text.disabled,
                      }}
                    >
                      Empty
                    </Typography>
                  ) : (
                    watchlist.symbols.map((sym) => {
                      const valid = validSymbols.has(sym);
                      const item = (
                        <ListItemButton
                          dense
                          disableGutters
                          disabled={!valid}
                          onClick={
                            valid
                              ? () => {
                                  onPick(sym);
                                  close();
                                }
                              : undefined
                          }
                          sx={{
                            minHeight: 0,
                            borderRadius: 1,
                            py: 0.5,
                            pl: '6px',
                            pr: 1.25,
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08,
                              ),
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            {sym}
                          </Typography>
                        </ListItemButton>
                      );
                      // Disabled buttons swallow pointer events, so wrap the
                      // invalid ones in a span for the tooltip to fire.
                      return valid ? (
                        <Box key={sym}>{item}</Box>
                      ) : (
                        <SGTooltip
                          key={sym}
                          title="No HIRO data — can't sync to a group"
                          placement="left"
                        >
                          <Box>{item}</Box>
                        </SGTooltip>
                      );
                    })
                  )}
                </Box>
              ))
            )}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default CanvasGroupWatchlistButton;
